import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Image as ImageIcon,
  Lock,
  AlertTriangle,
  Plus,
  Loader2,
  RefreshCw,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import type { DrawingFigureSummary, DrawingSourceFigure } from '@/api/client';
import {
  deleteMatterFigure, patchDrawingFigure, getDrawingSourceFiguresWhyEmpty,
} from '@/api/client';
import type { DrawingSourceFiguresWhyEmpty } from '@/api/client';
import { useDrawingStore } from '@/store/drawingStore';
import { stripMarkdown } from '@/lib/stripMarkdown';
import { RecentGenerationsPanel } from './RecentGenerationsPanel';

interface Props {
  matterId: string;
  figures: DrawingFigureSummary[];
  sourceFigures: DrawingSourceFigure[];
  activeFigureId: string | null;
  onSelect: (figureId: string) => void;
  onImport: (sourceMatterFigureId: number) => void;
  onSync: (drawingFigureId: string) => void;
  onDiscard: (drawingFigureId: string) => Promise<unknown>;
  /** Slice 5F — drafter clicked the Acknowledge button on a row whose
   * source IDF figure was removed from claims-side. */
  onAcknowledgeSourceRemoved: (drawingFigureId: string) => void;
  pendingImportSourceIds: Set<number>;
  pendingSyncIds: Set<string>;
}

/** Phase 5E — gallery is now interactive. Click + on an Available
 * row triggers the import; each In Drawing row exposes a sync icon
 * when `source_stale` and a discard icon. The Generate + Upload
 * trigger lives in the top header (DrawingPage) so the rail has
 * room for the lists. */
export default function FigureGallery({
  matterId,
  figures,
  sourceFigures,
  activeFigureId,
  onSelect,
  onImport,
  onSync,
  onDiscard,
  onAcknowledgeSourceRemoved,
  pendingImportSourceIds,
  pendingSyncIds,
}: Props) {
  // BUG-018 — preview lightbox + optimistic-delete state for the
  // Available from IDF rows. Local-deletion ids hide rows
  // immediately on confirm; the source-figures fetch on the next
  // workbench load won't return them anyway because the backend
  // soft-delete filters them out.
  const [previewSourceFigure, setPreviewSourceFigure] = useState<DrawingSourceFigure | null>(null);
  const [locallyDeletedSourceIds, setLocallyDeletedSourceIds] = useState<Set<number>>(new Set());

  // CHUNK 1 simplification — find the active figure to render its
  // editable description + drafter-note panels right below the
  // gallery list. Frozen figures show the text read-only.
  const activeFigure = figures.find((f) => f.id === activeFigureId) || null;

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="figure-gallery">
      <Section
        title="IN DRAWING"
        countLabel={`${figures.length}`}
        emptyMessage="No figures yet — import one below or upload."
        grow
      >
        <ul className="space-y-1.5 px-2 pb-2" role="list">
          {figures.map((f) => (
            <FigureRow
              key={f.id}
              figure={f}
              active={f.id === activeFigureId}
              syncing={pendingSyncIds.has(f.id)}
              onSelect={onSelect}
              onSync={onSync}
              onDiscard={onDiscard}
              onAcknowledgeSourceRemoved={onAcknowledgeSourceRemoved}
            />
          ))}
        </ul>
      </Section>

      {activeFigure && (
        <FigureMetadataPanels
          matterId={matterId}
          figure={activeFigure}
        />
      )}

      <div className="border-t border-zinc-200" />

      <Section
        title="AVAILABLE FROM IDF"
        countLabel={`${sourceFigures.length}`}
        emptyMessage={<SourceFiguresEmptyState matterId={matterId} />}
        grow
      >
        <ul className="space-y-1.5 px-2 pb-2" role="list">
          {sourceFigures
            .filter((sf) => !locallyDeletedSourceIds.has(sf.id))
            .map((sf) => (
              <ImportableFigureRow
                key={sf.id}
                matterId={matterId}
                figure={sf}
                importing={pendingImportSourceIds.has(sf.id)}
                onImport={onImport}
                onPreview={() => setPreviewSourceFigure(sf)}
                onLocallyDeleted={() => setLocallyDeletedSourceIds((prev) => {
                  const next = new Set(prev);
                  next.add(sf.id);
                  return next;
                })}
              />
            ))}
        </ul>
      </Section>

      {/* Recent Generations — chunk-2 durable surface for AI gen
          history. Renders nothing when the matter has zero jobs. */}
      <RecentGenerationsPanel matterId={matterId} figures={figures} />
      {/* Action stack (Generate with AI + Upload from disk) +
          file picker hoisted into DrawingPage's top header so the
          rail has more vertical room for the lists above. */}
      {previewSourceFigure && (
        <SourceFigurePreviewModal
          figure={previewSourceFigure}
          importing={pendingImportSourceIds.has(previewSourceFigure.id)}
          onClose={() => setPreviewSourceFigure(null)}
          onImport={() => {
            onImport(previewSourceFigure.id);
            setPreviewSourceFigure(null);
          }}
        />
      )}
    </div>
  );
}

/** What the picker says when it has nothing to offer.
 *
 * It used to assert "every IDF figure has been imported (or none were
 * detected)" — two happy explanations, stated without checking either.
 * When a disclosure carrying 34 drawings produced no figures because
 * extraction had failed, that sentence sent the drafter looking in
 * the wrong place. Now the server is asked, and an extraction failure
 * is shown as one: which document, how many images it carried, and
 * what went wrong. */
function SourceFiguresEmptyState({ matterId }: { matterId: string }) {
  const [why, setWhy] = useState<DrawingSourceFiguresWhyEmpty | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    getDrawingSourceFiguresWhyEmpty(matterId)
      .then((w) => { if (live) setWhy(w); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [matterId]);

  if (failed) {
    return <span>No figures available to import.</span>;
  }
  if (!why) {
    return <span className="text-zinc-400">Checking…</span>;
  }

  const isProblem = why.reason === 'extraction_failed';
  return (
    <div
      data-testid="source-figures-empty"
      data-reason={why.reason}
      className={isProblem ? 'text-amber-800' : undefined}
    >
      {isProblem && (
        <AlertTriangle size={12} className="mr-1 inline align-[-1px]" />
      )}
      {why.message}
    </div>
  );
}

// ─── CHUNK 1 simplification — figure metadata editor ─────────────
//
// Renders a Description block and a Drafter-notes block under the
// active figure's gallery row. Each panel is collapsible and edits
// in place: clicking the body opens a textarea, blur or Cmd+Enter
// commits via PATCH after a 400ms debounce. Empty string clears
// the column. Frozen figures render read-only with a Lock pill.

function FigureMetadataPanels({
  matterId, figure,
}: {
  matterId: string;
  figure: DrawingFigureSummary;
}) {
  const upsertFigureFromPatch = useDrawingStore((s) => s.upsertFigureFromPatch);
  return (
    <div
      // BUG-031b — capped height + internal scroll so an expanded
      // Description doesn't push the IDF list off the sidebar. The
      // collapsible fields default to closed (only the section
      // headers show) for the same reason; drafters expand on
      // demand.
      className="flex-shrink-0 max-h-[40vh] overflow-y-auto border-y border-zinc-100 bg-zinc-50 px-3 py-3"
      data-testid="figure-metadata-panels"
    >
      <CollapsibleField
        label="Description"
        // Description is the stored caption flowing into the spec's
        // Brief Description of Drawings. Edited inline alongside
        // Drafter notes — same CollapsibleField component, same
        // debounced auto-save + onBlur commit + Ctrl/Cmd+Enter
        // explicit save semantics. Frozen figures fall back to
        // read-only via ``readOnly={figure.is_frozen}`` so the
        // snapshot stays immutable; the backend PATCH endpoint
        // also rejects with 409 ``error_code='figure_frozen'`` as
        // a defence-in-depth backstop.
        displayMode="editable"
        rows={5}
        readOnly={figure.is_frozen}
        figureId={figure.id}
        value={figure.imported_description || ''}
        emptyHint="No description yet — add one here, or via the figure dialog / Brief Description of Drawings section."
        onSave={async (text) => {
          const r = await patchDrawingFigure(matterId, figure.id, {
            imported_description: text,
          });
          upsertFigureFromPatch(r.figure);
        }}
        testIdSuffix="description"
      />
      <CollapsibleField
        label="Drafter notes"
        displayMode="editable"
        rows={6}
        readOnly={figure.is_frozen}
        figureId={figure.id}
        value={figure.drafters_note || ''}
        emptyHint="Private scratch notes — visible only here, never exported."
        onSave={async (text) => {
          const r = await patchDrawingFigure(matterId, figure.id, {
            drafters_note: text,
          });
          upsertFigureFromPatch(r.figure);
        }}
        testIdSuffix="drafters-note"
      />
    </div>
  );
}

function CollapsibleField({
  label, value, emptyHint, readOnly, figureId, onSave, testIdSuffix,
  displayMode = 'editable', rows = 3, defaultOpen = false,
}: {
  label: string;
  value: string;
  emptyHint: string;
  readOnly: boolean;
  figureId: string;
  onSave: (text: string) => Promise<void>;
  testIdSuffix: string;
  displayMode?: 'editable' | 'readonly';
  rows?: number;
  /** BUG-031b — default-closed in the drawings sidebar so the
   * 5/6-row text panels don't push the IDF list off-screen.
   * Drafter clicks the header to expand. */
  defaultOpen?: boolean;
}) {
  // Reset local state whenever the active figure changes so the
  // textarea reflects the new figure's content rather than the
  // previous figure's stale draft.
  const [open, setOpen] = useState(defaultOpen);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setDraft(value);
    setError(null);
    setSavedAt(null);
  }, [figureId, value]);

  // Debounced auto-save. Each keystroke pushes the timer 400ms
  // forward; only after the user stops typing for 400ms does the
  // PATCH fire. The timer ref persists across renders so each
  // keystroke clears the previous one cleanly.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCommit = useCallback(async (next: string) => {
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setSavedAt(Date.now());
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: { detail?: string } } }; message?: string })
          ?.response?.data?.detail?.detail
        ?? (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        ?? (e as { message?: string })?.message
        ?? 'Save failed.';
      setError(typeof msg === 'string' ? msg : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [onSave]);

  const onChange = useCallback((next: string) => {
    setDraft(next);
    setError(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (readOnly) return;
    debounceRef.current = setTimeout(() => { void onCommit(next); }, 400);
  }, [onCommit, readOnly]);

  const onBlur = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (readOnly) return;
    if (draft !== value) void onCommit(draft);
  }, [draft, value, onCommit, readOnly]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (readOnly) return;
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (draft !== value) void onCommit(draft);
      return;
    }
    if (e.key === 'Escape') {
      // Revert to the last server-known value AND cancel any
      // pending debounced save. Safe within the auto-save model:
      // ``value`` always reflects the last successful PATCH (the
      // useEffect at the top of the component re-syncs ``draft``
      // to ``value`` whenever it changes), so this is "undo what
      // I just typed" rather than "undo what's saved".
      e.preventDefault();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      setDraft(value);
      setError(null);
    }
  }, [draft, value, onCommit, readOnly]);

  const status = error
    ? <span className="text-red-700">{error}</span>
    : saving
      ? <span className="text-zinc-500">Saving…</span>
      : savedAt
        ? <span className="text-green-700">Saved.</span>
        : null;

  return (
    <div className="mb-3 last:mb-0" data-testid={`metadata-${testIdSuffix}`}>
      <div className="mb-1 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-700"
          data-testid={`metadata-${testIdSuffix}-toggle`}
        >
          <span aria-hidden>{open ? '▼' : '▶'}</span>
          {label}
          {readOnly && displayMode === 'editable' && (
            <span className="ml-1 inline-flex items-center gap-0.5 rounded-sm bg-zinc-200 px-1 py-0.5 text-[9px] font-medium text-zinc-700">
              <Lock className="h-2.5 w-2.5" /> frozen
            </span>
          )}
        </button>
        <span className="text-[10px]">{status}</span>
      </div>
      {open && displayMode === 'readonly' && (
        // BUG-028 — read-only caption view. ``stripMarkdown`` peels
        // the vision-pass ``**bold**`` markers so the panel shows
        // clean prose. The fixed ``min-h`` keeps short captions
        // from collapsing into a 1-line slit; longer captions
        // scroll inside the box.
        <div
          className="w-full whitespace-pre-wrap break-words rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-xs leading-snug text-zinc-800 overflow-y-auto"
          style={{ minHeight: `${rows * 1.25}rem`, maxHeight: '14rem' }}
          data-testid={`metadata-${testIdSuffix}-readonly`}
        >
          {value
            ? stripMarkdown(value)
            : <span className="italic text-zinc-400">{emptyHint}</span>}
        </div>
      )}
      {open && displayMode === 'editable' && (
        <textarea
          value={draft}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          rows={rows}
          readOnly={readOnly}
          placeholder={emptyHint}
          className="w-full resize-y rounded-sm border border-zinc-300 bg-white px-2 py-1.5 text-xs leading-snug text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[#002FA7] disabled:opacity-60 read-only:bg-zinc-100"
          data-testid={`metadata-${testIdSuffix}-textarea`}
        />
      )}
    </div>
  );
}


interface SectionProps {
  title: string;
  countLabel?: string;
  /** A node, not just a string: an empty list sometimes has to
   * explain itself rather than assert a reason it has not checked. */
  emptyMessage?: React.ReactNode;
  children: React.ReactNode;
}

function Section({
  title, countLabel, emptyMessage, children, grow = false,
}: SectionProps & { grow?: boolean }) {
  // ``grow`` toggles ``flex-1`` so the IN DRAWING and AVAILABLE
  // FROM IDF sections share the remaining vertical space (BUG-031b
  // sidebar fix). Without it the lists collapsed to natural height
  // and the active-figure metadata block pushed the IDF list
  // off-screen.
  return (
    <section className={[
      'flex min-h-0 flex-col',
      grow ? 'flex-1' : '',
    ].join(' ')}>
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-zinc-200 bg-white px-3 py-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
          {title}
        </h2>
        {countLabel !== undefined && (
          <span className="text-[11px] font-medium text-zinc-500">{countLabel}</span>
        )}
      </header>
      <div className="flex-1 overflow-y-auto">
        {isEmptyList(children) ? (
          <div className="px-3 py-4 text-xs text-zinc-500">{emptyMessage}</div>
        ) : (
          children
        )}
      </div>
    </section>
  );
}

function isEmptyList(children: React.ReactNode): boolean {
  let isEmpty = true;
  React.Children.forEach(children, (c) => {
    if (
      React.isValidElement(c)
      && c.type === 'ul'
      && React.Children.count((c.props as { children?: React.ReactNode }).children) > 0
    ) {
      isEmpty = false;
    } else if (React.isValidElement(c) && c.type !== 'ul') {
      isEmpty = false;
    }
  });
  return isEmpty;
}

interface FigureRowProps {
  figure: DrawingFigureSummary;
  active: boolean;
  syncing: boolean;
  onSelect: (figureId: string) => void;
  onSync: (figureId: string) => void;
  /** BUG-002 — returns a promise so the row can await the
   *  discard and clear its in-flight spinner cleanly. The
   *  store returns ``false`` on error; the row uses the return
   *  value to keep the flag set or clear it. */
  onDiscard: (figureId: string) => Promise<unknown>;
  onAcknowledgeSourceRemoved: (figureId: string) => void;
}

function FigureRow({
  figure, active, syncing, onSelect, onSync, onDiscard, onAcknowledgeSourceRemoved,
}: FigureRowProps) {
  // BUG-002 — in-flight delete guard. Without this the drafter
  // could double-click trash before the first promise resolves,
  // queueing two DELETE requests for the same id. The second
  // 404s server-side once the first lands; the FE saw the 404
  // as "delete failed" and surfaced a misleading error. Local
  // state is sufficient — the button is per-row and the delete
  // resolves quickly under the new surgical store path.
  const [deleting, setDeleting] = useState(false);
  const hasIssues = figure.unmapped_terms_count > 0 || figure.source_stale;
  const reason = (figure as DrawingFigureSummary & { source_stale_reason?: string | null })
    .source_stale_reason;
  const stalePillCopy = (() => {
    if (!figure.source_stale) return null;
    if (reason === 'source_removed') return 'source removed';
    return 'source updated';
  })();
  const showAcknowledge = figure.source_stale && reason === 'source_removed';
  const showSync = figure.source_stale && reason !== 'source_removed';

  return (
    <li>
      <div
        className={[
          'group flex w-full items-start gap-2 rounded-sm border px-2 py-2',
          'transition-colors',
          active
            ? 'border-[#002FA7] bg-[#002FA7]/5 ring-1 ring-[#002FA7]'
            : 'border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50',
        ].join(' ')}
        data-testid={`figure-row-${figure.id}`}
        data-active={active ? 'true' : 'false'}
      >
        <button
          type="button"
          className="flex flex-1 items-start gap-2 text-left"
          onClick={() => onSelect(figure.id)}
          aria-current={active ? 'true' : undefined}
        >
          {/* FT-002 (A2) — thumbnail cache-bust. After a freeze
              recovery the figure row's updated_at bumps but the
              browser kept serving the stale preview byte; appending
              the bumped token forces a refetch so the gallery shows
              the correct image without a manual reload. */}
          <FigureThumb
            src={`${figure.thumb_url}?_v=${
              figure.updated_at ? Date.parse(figure.updated_at) || 0 : 0
            }`}
            alt={figure.fig_label || ''}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-sm font-medium text-zinc-900">
                {figure.fig_label || `Figure ${figure.sequence_num}`}
              </span>
              {figure.is_frozen && (
                <span
                  className="inline-flex items-center gap-1 rounded-sm bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600"
                  title="This figure is frozen — its analysis is locked."
                >
                  <Lock className="h-3 w-3" aria-hidden />
                  Frozen
                </span>
              )}
            </div>
            <p className="line-clamp-2 text-xs text-zinc-500">
              {/* NB-34 — a figure described on the Specification-draft
                  intake (or imported with its disclosure description)
                  and given no caption of its own shows that
                  description rather than "Imported from IDF." */}
              {figure.caption
                || (figure.imported_description || '').split('\n')
                  .map((l) => l.trim()).find(Boolean)
                || (figure.origin === 'idf'
                  ? 'Imported from IDF.'
                  : figure.origin === 'ai_generated'
                    ? 'AI-generated figure.'
                    : 'Uploaded — add a caption.')}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {figure.origin === 'ai_generated' && (
                <Badge tone="indigo" icon={<Sparkles className="h-3 w-3" />}>
                  AI{figure.generator ? ` · ${figure.generator}` : ''}
                </Badge>
              )}
              {figure.numerals_count > 0 && (
                <Badge tone="zinc">{figure.numerals_count} numerals</Badge>
              )}
              {hasIssues && (
                <Badge tone="amber" icon={<AlertTriangle className="h-3 w-3" />}>
                  {stalePillCopy}
                  {stalePillCopy && figure.unmapped_terms_count > 0 && ' · '}
                  {figure.unmapped_terms_count > 0
                    && `${figure.unmapped_terms_count} unmapped`}
                </Badge>
              )}
            </div>
          </div>
        </button>
        <div className="flex flex-shrink-0 flex-col items-center gap-1 pt-1">
          {showSync && (
            <button
              type="button"
              className="inline-flex h-6 w-6 items-center justify-center rounded-sm border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={(e) => {
                e.stopPropagation();
                if (
                  // eslint-disable-next-line no-restricted-globals
                  confirm(
                    'The IDF figure was updated. Sync the description from claims-side?\n\nYour placed numerals and notes will be kept.',
                  )
                ) {
                  onSync(figure.id);
                }
              }}
              disabled={syncing}
              title="Pull the latest description from the IDF and re-prime claim mapping. The image stays unchanged so your markup is preserved."
              aria-label="Sync from source IDF"
              data-testid={`figure-sync-${figure.id}`}
            >
              {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            </button>
          )}
          {showAcknowledge && (
            <button
              type="button"
              className="inline-flex h-6 px-1.5 items-center justify-center rounded-sm border border-amber-200 bg-amber-50 text-[10px] font-medium text-amber-700 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={(e) => {
                e.stopPropagation();
                if (
                  // eslint-disable-next-line no-restricted-globals
                  confirm(
                    'The IDF figure was deleted from claims-side. Acknowledge to clear this badge?\n\nThe drawing figure persists unchanged.',
                  )
                ) {
                  onAcknowledgeSourceRemoved(figure.id);
                }
              }}
              disabled={syncing}
              title="Acknowledge that the source IDF figure has been removed."
              aria-label="Acknowledge source removed"
              data-testid={`figure-acknowledge-${figure.id}`}
            >
              {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Ack'}
            </button>
          )}
          <button
            type="button"
            // Phase 4c — accessible hit area. Was opacity-0 +
            // group-hover:opacity-100, which lost the icon mid-
            // interaction if the hover state dropped during the
            // BUG-001 freeze. Now faintly visible at rest (opacity-30)
            // and full opacity on hover OR focus — keyboard
            // navigation reaches it too.
            //
            // BUG-002 — in-flight delete spinner. Without it a
            // drafter could double-click trash; the second
            // promise raced the first and surfaced a confusing
            // 404 "delete failed" after the row was already
            // gone. ``deleting`` also disables the button so
            // the click can't repeat.
            className="inline-flex h-6 w-6 items-center justify-center rounded-sm border border-zinc-200 bg-white text-zinc-400 opacity-30 transition-opacity hover:bg-red-50 hover:text-red-600 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300 group-hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={deleting}
            onClick={async (e) => {
              e.stopPropagation();
              if (deleting) return;
              if (
                // eslint-disable-next-line no-restricted-globals
                !confirm(`Discard ${figure.fig_label || 'this figure'}? This removes it from the workbench. The IDF source is unaffected.`)
              ) {
                return;
              }
              setDeleting(true);
              try {
                // ``onDiscard`` resolves once the store's
                // surgical state update lands. On success the
                // store filters this figure out of ``figures``
                // and this row unmounts (no further setState
                // needed). On failure the store writes
                // mutationError and the row stays — we clear
                // the deleting flag so the drafter can retry.
                await onDiscard(figure.id);
              } finally {
                // React 18: setState on an unmounted component
                // is a no-op with a console warning suppressed
                // in production. We accept the warning in dev
                // rather than threading a mountedRef through
                // for one branch.
                setDeleting(false);
              }
            }}
            title="Discard this drawing figure"
            aria-label="Discard figure"
            data-testid={`figure-discard-${figure.id}`}
          >
            {deleting
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Trash2 className="h-3 w-3" />}
          </button>
        </div>
      </div>
    </li>
  );
}

interface ImportableFigureRowProps {
  matterId: string;
  figure: DrawingSourceFigure;
  importing: boolean;
  onImport: (sourceMatterFigureId: number) => void;
  /** BUG-018 — open the lightbox preview modal owned by the
   * parent FigureGallery. */
  onPreview: () => void;
  /** BUG-018 — soft-delete succeeded; parent removes the row
   * optimistically from its filtered list. */
  onLocallyDeleted: () => void;
}

function ImportableFigureRow({
  matterId, figure, importing, onImport, onPreview, onLocallyDeleted,
}: ImportableFigureRowProps) {
  // BUG-018 — three states: idle, armed (one click puts the
  // trash button into a confirm-style red, the second click
  // commits), deleting (network call in flight). Confirm flips
  // off automatically on blur so a stray click doesn't leave
  // the affordance armed forever.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onTrashClick = useCallback(async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await deleteMatterFigure(matterId, figure.id);
      onLocallyDeleted();
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message || 'Delete failed.';
      setError(msg);
      setDeleting(false);
      setConfirmDelete(false);
    }
  }, [confirmDelete, matterId, figure.id, onLocallyDeleted]);

  return (
    <li>
      <div
        className={[
          'flex items-start gap-2 rounded-sm border border-dashed px-2 py-2',
          importing ? 'border-[#002FA7] bg-[#002FA7]/5' : 'border-zinc-300 bg-white',
        ].join(' ')}
        data-testid={`importable-row-${figure.id}`}
      >
        <button
          type="button"
          onClick={onPreview}
          className="rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          title="Open full preview"
          aria-label={`Preview ${figure.fig_label || 'figure'}`}
          data-testid={`preview-button-${figure.id}`}
        >
          <FigureThumb
            src={figure.thumb_url}
            alt={figure.fig_label || figure.source_label || ''}
          />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-sm font-medium text-zinc-900">
              {figure.fig_label || figure.source_label || `IDF figure ${figure.id}`}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-zinc-200 bg-white text-zinc-600 hover:bg-[#002FA7] hover:text-white disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                disabled={importing || deleting}
                onClick={() => onImport(figure.id)}
                title={importing ? 'Importing…' : 'Import this figure into the drawing workbench.'}
                aria-label={`Import ${figure.fig_label || 'figure'}`}
                data-testid={`import-button-${figure.id}`}
              >
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              </button>
              <button
                type="button"
                className={[
                  'inline-flex h-7 w-7 items-center justify-center rounded-sm border transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                  confirmDelete
                    ? 'border-red-300 bg-red-50 text-red-600 hover:bg-red-100'
                    : 'border-zinc-200 bg-white text-zinc-500 hover:bg-red-50 hover:text-red-600',
                ].join(' ')}
                disabled={deleting || importing}
                onClick={onTrashClick}
                onBlur={() => setConfirmDelete(false)}
                title={
                  confirmDelete
                    ? `Click again to confirm: remove ${figure.fig_label || 'this figure'} from the IDF pool. This cannot be undone.`
                    : `Remove ${figure.fig_label || 'this figure'} from the IDF pool.`
                }
                aria-label={confirmDelete
                  ? `Confirm remove ${figure.fig_label || 'figure'}`
                  : `Remove ${figure.fig_label || 'figure'}`}
                data-testid={`delete-button-${figure.id}`}
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <p className="line-clamp-2 text-xs text-zinc-500">
            {stripMarkdown(figure.description) || 'No description from IDF extraction.'}
          </p>
          {confirmDelete && !deleting && (
            <p className="mt-0.5 text-[11px] font-medium text-red-700">
              Click ✕ again to confirm — this cannot be undone.
            </p>
          )}
          {error && (
            <p className="mt-0.5 text-[11px] text-red-700">{error}</p>
          )}
        </div>
      </div>
    </li>
  );
}

// BUG-018 — full-image lightbox with the cleaned description and
// the same Import action the inline + button fires. Dismissible
// via Escape key, the close button, or clicking the backdrop.
function SourceFigurePreviewModal({
  figure, importing, onClose, onImport,
}: {
  figure: DrawingSourceFigure;
  importing: boolean;
  onClose: () => void;
  onImport: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const description = stripMarkdown(figure.description)
    || 'No description from IDF extraction.';
  const label = figure.fig_label || figure.source_label || `IDF figure ${figure.id}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="idf-preview-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="idf-preview-modal"
    >
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-md bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
          <h2
            id="idf-preview-modal-title"
            className="text-sm font-semibold text-zinc-900"
          >
            {label}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"
            aria-label="Close preview"
            data-testid="idf-preview-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-auto px-4 py-4">
          <div className="flex items-center justify-center bg-zinc-50 p-3">
            <img
              src={figure.full_url}
              alt={label}
              className="max-h-[60vh] max-w-full object-contain"
            />
          </div>
          <p className="mt-3 whitespace-pre-wrap text-xs leading-relaxed text-zinc-700">
            {description}
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center justify-center rounded-sm border border-zinc-300 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
            data-testid="idf-preview-close-btn"
          >
            Close
          </button>
          <button
            type="button"
            onClick={onImport}
            disabled={importing}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-sm bg-[#002FA7] px-3 text-sm font-medium text-white hover:bg-[#001F70] disabled:opacity-50"
            data-testid="idf-preview-import"
          >
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {importing ? 'Importing…' : 'Import figure'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Badge({
  tone, icon, children,
}: {
  tone: 'zinc' | 'amber' | 'indigo';
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  const palette = tone === 'amber'
    ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
    : tone === 'indigo'
      ? 'bg-indigo-50 text-[#002FA7] ring-1 ring-indigo-200'
      : 'bg-zinc-100 text-zinc-600';
  return (
    <span className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-medium ${palette}`}>
      {icon}
      {children}
    </span>
  );
}

function FigureThumb({ src, alt }: { src?: string; alt?: string } = {}) {
  // BUG-015 — wire the real raster URL with an icon-only fallback
  // for figures whose preview_path / image_path is missing
  // (vision was off at upload, file moved, etc.). Toggle state
  // tracks the <img> onError signal so the placeholder lights up
  // without blowing away the layout slot.
  const [errored, setErrored] = useState(false);
  const showFallback = !src || errored;
  return (
    <div
      className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-sm border border-zinc-200 bg-zinc-50"
      aria-hidden={showFallback}
    >
      {showFallback ? (
        <ImageIcon className="h-5 w-5 text-zinc-300" />
      ) : (
        <img
          src={src}
          alt={alt || ''}
          className="h-full w-full object-contain"
          onError={() => setErrored(true)}
          loading="lazy"
        />
      )}
    </div>
  );
}
